import { Stack, Tabs } from '@mantine/core';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import styles from '@/components/library/library.module.css';
import LibraryCard from '@/components/library-card';
import useFlow from '@/hooks/use-flow';
import useFlowStore from '@/store/flow';
import type { TGroupItemFrontend, TLibraryItemFrontend } from '@/types/common';
import { AppEvent } from '@/types/event-emitter.types.ts';
import { emitter } from '@/utils/event-emitter.ts';

const Library = ({ onClose }: { onClose: () => void }) => {
	const [currentTab, setCurrentTab] = useState<string>('all_items');
	const { library, itemsDictionary, addItem } = useFlowStore(
		useShallow((state) => ({
			library: state.library,
			itemsDictionary: state.itemsDictionary,
			addItem: state.addItem
		}))
	);

	const { saveItem } = useFlow();

	const allItems = useMemo(() => {
		return library.flatMap((lib) =>
			lib.items.map((item) => ({
				...item,
				libType: lib.idn,
				libColor: lib.color
			}))
		);
	}, [library]);

	const tabs = useMemo(() => {
		return [
			{ id: 'all_items', label: 'All Items', color: '' },
			...library.map((lib) => ({
				id: lib.idn,
				label: lib.title,
				color: lib.color
			}))
		];
	}, [library]);

	const handleAdd = useCallback(
		(type: string, item: TLibraryItemFrontend) => {
			const newItem = {
				idn: item.idn,
				title: item.title,
				description: item.description,
				body: item.body,
				mentions: [],
				is_from_library: true
			} satisfies TGroupItemFrontend;

			saveItem(newItem, type);
			addItem(type, newItem);
			emitter.emit(AppEvent.SCROLL_TO_CARD, { cardId: item.idn });
			onClose();
		},
		[onClose, saveItem, addItem]
	);

	const onChangeTab = (tab: string | null) => {
		if (!tab) {
			return;
		}
		setCurrentTab(tab);
	};
	return (
		<Tabs
			defaultValue="all_items"
			onChange={onChangeTab}
			value={currentTab}
		>
			<Tabs.List className={styles.tabList}>
				{tabs.map((lib) => (
					<Tabs.Tab
						className={clsx(styles.tabButton, {
							[styles.active]: lib.id === currentTab
						})}
						key={lib.id}
						value={lib.id}
					>
						{!!lib.color && (
							<div
								className={styles.indicator}
								style={{ '--type-color': lib.color } as React.CSSProperties}
							/>
						)}

						{lib.label}
					</Tabs.Tab>
				))}
			</Tabs.List>

			<Tabs.Panel
				key="all_items"
				value="all_items"
			>
				<Stack
					gap={16}
					pt={16}
				>
					{allItems.map((item) => (
						<LibraryCard
							color={item.libColor}
							data={item}
							isUsed={item.idn in itemsDictionary}
							key={item.idn}
							onAdd={handleAdd}
							type={item.libType}
						/>
					))}
				</Stack>
			</Tabs.Panel>
			{library.map((lib) => (
				<Tabs.Panel
					key={lib.idn}
					value={lib.idn}
				>
					<Stack
						gap={16}
						pt={16}
					>
						{lib.items.map((item) => (
							<LibraryCard
								color={lib.color}
								data={item}
								isUsed={item.idn in itemsDictionary}
								key={item.idn}
								onAdd={handleAdd}
								type={lib.idn}
							/>
						))}
					</Stack>
				</Tabs.Panel>
			))}
		</Tabs>
	);
};

export default Library;
